import {useState, useCallback, useMemo} from 'react';

import {sortDropdownOptionsAlphanumerically} from '@utils/sorters';
import {GetStageCategories} from '@graphql/stageCategory/query';

const EMPTY_PROPERTY_LICENCE_CONTENTS = [];

export default function useStageCategories({
    client,
    propertyLicenceContents = EMPTY_PROPERTY_LICENCE_CONTENTS,
}) {
    const [firstStageCategories, setFirstStageCategories] = useState([]);
    const [secondStageCategories, setSecondStageCategories] = useState([]);

    const [firstStageCategoryValue, setFirstStageCategoryValue] = useState(null);
    const [secondStageCategoryValue, setSecondStageCategoryValue] = useState(null);

    const [secondStageCategoriesLoading, setSecondStageCategoriesLoading] = useState(false);
    const [firstStageCategoriesLoading, setFirstStageCategoriesLoading] = useState(false);

    const usedStageCategoryIds = useMemo(() => {
        return propertyLicenceContents.filter(c => c.stage_category).map(c => parseInt(c.stage_category.id));
    }, [propertyLicenceContents]);

    const fetchFirstStageCategories = useCallback(
        async (sportIds) => {
            if (!sportIds?.length) {
                setFirstStageCategories([]);
                setFirstStageCategoriesLoading(false);

                return [];
            }

            setFirstStageCategories([]);
            setFirstStageCategoriesLoading(true);

            try {
                const response = await client.query({
                    query: GetStageCategories,
                    variables: {sport: sportIds},
                });

                const data = response.data.stageCategories
                    ? sortDropdownOptionsAlphanumerically(
                        response.data.stageCategories.mapDataForDropdownWithIntVal()
                    )
                    : [];

                setFirstStageCategories(data);

                return data;
            } catch {
                setFirstStageCategories([]);

                return [];
            } finally {
                setFirstStageCategoriesLoading(false);
            }
        },
        [client]
    );

    const fetchSecondStageCategories = useCallback(
        async (firstStageIds) => {
            if (!firstStageIds || 0 === firstStageIds?.length) {
                setSecondStageCategories([]);
                setSecondStageCategoriesLoading(false);

                return [];
            }

            if (!firstStageIds?.length) {
                firstStageIds = [firstStageIds];
            }

            setSecondStageCategories([]);
            setSecondStageCategoriesLoading(true);

            try {
                const response = await client.query({
                    query: GetStageCategories,
                    variables: {
                        parent: firstStageIds,
                        excludedIds: usedStageCategoryIds,
                    },
                });

                const data = response.data.stageCategories
                    ? sortDropdownOptionsAlphanumerically(
                        response.data.stageCategories.mapDataForDropdownWithIntVal()
                    )
                    : [];

                setSecondStageCategories(data);

                return data;
            } catch {
                setSecondStageCategories([]);

                return [];
            } finally {
                setSecondStageCategoriesLoading(false);
            }
        },
        [client, usedStageCategoryIds]
    );

    const handleFirstStageCategoryChange = useCallback(
        (event, data) => {
            const value = data?.value ?? data ?? null;

            setFirstStageCategoryValue(value);

            if (!value?.length) {
                setSecondStageCategoryValue(null);
                setSecondStageCategories([]);
            }

            return fetchSecondStageCategories(value);
        },
        [fetchSecondStageCategories]
    );

    const handleSecondStageCategoryChange = useCallback(
        (event, data) => {
            setSecondStageCategoryValue(data?.value ?? null);
        },
        []
    );

    const reset = useCallback(() => {
        setFirstStageCategoryValue(null);
        setSecondStageCategoryValue(null);
        setFirstStageCategories([]);
        setSecondStageCategories([]);
        setFirstStageCategoriesLoading(false);
        setSecondStageCategoriesLoading(false);
    }, []);

    const resetToFirstCategory = useCallback(() => {
        setSecondStageCategoryValue(null);
        setSecondStageCategories([]);
        fetchSecondStageCategories(firstStageCategoryValue);
    }, [firstStageCategoryValue, fetchSecondStageCategories]);

    return {
        firstStageCategories,
        secondStageCategories,
        firstStageCategoriesLoading,
        secondStageCategoriesLoading,
        firstStageCategoryValue,
        secondStageCategoryValue,
        fetchFirstStageCategories,
        fetchSecondStageCategories,
        setFirstStageCategoryValue,
        setSecondStageCategoryValue,
        setFirstStageCategoriesLoading,
        setSecondStageCategoriesLoading,
        handleFirstStageCategoryChange,
        handleSecondStageCategoryChange,
        setFirstStageCategories,
        setSecondStageCategories,
        reset,
        resetToFirstCategory,
    };
}
